import { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

function normalizeOptions(options = []) {
  return options
    .map((opt, index) => {
      if (opt == null) {
        return null;
      }

      if (typeof opt === "string" || typeof opt === "number") {
        const stringValue = String(opt);
        return { value: stringValue, label: stringValue };
      }

      const hasExplicitValue = opt.value !== undefined && opt.value !== null;
      const fallbackValue =
        opt.label === undefined || opt.label === null ? "" : String(opt.label);
      const valueSource = hasExplicitValue ? opt.value : fallbackValue;
      if (valueSource === undefined || valueSource === null) {
        return null;
      }
      const value = String(valueSource);
      const label =
        opt.label === undefined || opt.label === null ? value : String(opt.label);

      return {
        value,
        label,
        key: opt.key ?? `${value}-${index}`,
      };
    })
    .filter((opt) => opt && opt.value !== undefined && opt.value !== null);
}

export default function SearchableSelect({
  label,
  placeholder = "Selecteer een optie",
  name,
  value,
  options,
  onChange,
  clearLabel = "Alle opties",
  wrapperClassName = "",
  inputClassName = "",
  labelHelpIcon,
  onLabelHelpClick,
  multiple = false,
  clearable = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const normalizedOptions = useMemo(
    () => normalizeOptions(options),
    [options]
  );

  const selectedValues = useMemo(() => {
    if (multiple) {
      return Array.isArray(value)
        ? value.map(String)
        : value
          ? [String(value)]
          : [];
    }
    return value ? [String(value)] : [];
  }, [multiple, value]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((opt) => opt.value === value),
    [normalizedOptions, value]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return normalizedOptions;
    }
    const term = search.trim().toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(term)
    );
  }, [normalizedOptions, search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isOpen]);

  const handleSelect = (nextValue) => {
    if (multiple) {
      const next = String(nextValue);
      const set = new Set(selectedValues);
      if (set.has(next)) {
        set.delete(next);
      } else {
        set.add(next);
      }
      onChange?.(Array.from(set));
      return;
    }
    onChange?.(nextValue);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.(multiple ? [] : "");
    if (!multiple) {
      setIsOpen(false);
    }
  };

  const selectedSummary = useMemo(() => {
    if (!multiple) {
      return selectedOption?.label || placeholder;
    }
    if (!selectedValues.length) {
      return placeholder;
    }
    const labels = selectedValues
      .map((val) => normalizedOptions.find((opt) => opt.value === val)?.label || val)
      .filter(Boolean);
    if (labels.length <= 2) {
      return labels.join(", ");
    }
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  }, [multiple, normalizedOptions, placeholder, selectedOption, selectedValues]);

  return (
    <div
      className={`form-group searchable-select ${wrapperClassName}`}
      ref={containerRef}
    >
      {label && (
        <label htmlFor={`${name || "select"}-trigger`} className="form-label">
          <span>{label}</span>
          {labelHelpIcon && (
            <img
              src={labelHelpIcon}
              alt="help"
              className="form-label-help"
              onClick={onLabelHelpClick}
            />
          )}
        </label>
      )}

      <button
        type="button"
        id={`${name || "select"}-trigger`}
        title={selectedSummary}
        className={`select-trigger form-select ${inputClassName} ${
          isOpen ? "open" : ""
        }`}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={`select-trigger-value ${
            multiple ? (selectedValues.length ? "" : "placeholder") : value ? "" : "placeholder"
          }`}
        >
          {selectedSummary}
        </span>
      </button>

      {isOpen && (
        <div className="select-dropdown">
          <div className="select-search">
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Zoek..."
              aria-label="Zoek"
            />
          </div>
          <div className="select-options" role="listbox">
            {clearable && (
              <button
                type="button"
                className={`option-item ${
                  multiple ? (selectedValues.length ? "" : "selected") : value ? "" : "selected"
                }`}
                onClick={handleClear}
              >
                {clearLabel}
              </button>
            )}
            {filteredOptions.length === 0 && (
              <div className="option-empty">Geen opties gevonden</div>
            )}
            {filteredOptions.map((opt) => {
              const isSelected = multiple
                ? selectedValues.includes(opt.value)
                : opt.value === value;

              return (
                <button
                  type="button"
                  key={opt.key || opt.value}
                  className={`option-item ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  <span className="option-label">{opt.label}</span>
                  {isSelected && <span className="option-check">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
